// Ink choice for text sitting on a colour fill.
//
// Lives in its own leaf module (not primitives.jsx) because three surfaces need
// it: the primitives themselves, the studio's accent picker (which previews the
// ink an author is about to get), and anything else measuring a fill. Keeping it
// here means the studio client doesn't pull ~6k lines of React into its bundle
// to ask one question, and there is no second copy to drift.
//
// primitives.jsx re-exports `prefersDarkText` and `inkOn` for slide code —
// see the note there about why it uses `export const` and not `export … from`.
import { BG, BG_ELEV, FG, ORANGE, CYAN, GREEN, PINK, PURPLE, LEMON, BLOSSOM, BLUSH, SCARLET } from './tokens';

// Returns true when a hex background calls for black text. Brand accents follow
// the APCA ink rule from DESIGN.md — white ink on the warm hues (orange, pink,
// violet, scarlet), near-black on cyan, lime and the pale cuts — because WCAG
// 2.1's luminance ratio calls dark text "passing" on the warm fills where it
// actually reads muddy. Non-brand colours fall back to the WCAG relative-
// luminance threshold. Pass an explicit `dark` prop to override.
//
// EVERY brand accent is listed explicitly, deliberately. The fallback is not a
// safety net here: it answers "near-black" for anything that isn't dark, and it
// gets SCARLET (#EC002A) wrong by a hair — L = 0.1800 against a 0.179 threshold,
// so a saturated warm red would take black ink. Anything added to accents.js
// needs an entry here, checked by eye rather than by the formula.
const APCA_ACCENT_INK = new Map([
  [ORANGE.toLowerCase(), false], // white ink
  [PINK.toLowerCase(), false], // white ink
  [PURPLE.toLowerCase(), false], // violet — white ink
  [SCARLET.toLowerCase(), false], // warm red — white ink (fallback would say black)
  [CYAN.toLowerCase(), true], // near-black ink
  [GREEN.toLowerCase(), true], // lime (also YELLOW) — near-black ink
  [LEMON.toLowerCase(), true], // bright yellow — near-black ink
  [BLOSSOM.toLowerCase(), true], // pale pink — near-black ink
  [BLUSH.toLowerCase(), true], // pale blush — near-black ink
]);

export const prefersDarkText = (hex: string): boolean => {
  // The runtime guard stays despite the `string` annotation: the deck slides
  // and the studio client that call this are still untyped .jsx.
  if (typeof hex !== 'string') return false;
  const apca = APCA_ACCENT_INK.get(hex.toLowerCase());
  if (apca !== undefined) return apca;
  const digits = hex.replace('#', '').match(/^([\da-f]{3}|[\da-f]{6})$/i)?.[1];
  if (!digits) return false;
  const h =
    digits.length === 3
      ? digits
          .split('')
          .map((c) => c + c)
          .join('')
      : digits;
  const lin = (c: string) => {
    const s = parseInt(c, 16) / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * lin(h.slice(0, 2)) + 0.7152 * lin(h.slice(2, 4)) + 0.0722 * lin(h.slice(4, 6));
  return L > 0.179;
};

// Ink colour for text sitting on an accent fill — bone on the warm hues,
// black on cyan/lime and the pale cuts (the APCA rule above).
export const inkOn = (bg: string): string => (prefersDarkText(bg) ? BG : FG);

// The UNFILLED part of a chart — a bar's track, a rail's remainder — carries a
// whisper of that chart's own accent instead of neutral grey, so a cyan chart
// reads cyan all the way across and the empty half stops looking like a hole in
// the slide (Jake, 2026-08-01). The default 16% over BG_ELEV is the mix Jake
// signed off on the coffee deck: enough hue to register at slide size, dark
// enough that the filled part still reads as the data.
//
// `mix` is the accent's share; raise it when the track sits on an elevated
// panel rather than the black slide, or the tint disappears into the panel.
// Non-hex input (a CSS gradient, a var()) falls back to the untinted surface.
export const chartTrack = (accent: string, mix = 0.16, surface: string = BG_ELEV): string => {
  const a = hexToRgb(accent);
  const s = hexToRgb(surface);
  if (!a || !s) return surface;
  const t = Math.min(1, Math.max(0, mix));
  // `0 | 1 | 2` rather than `number`: indexing a tuple with a literal is exact,
  // so noUncheckedIndexedAccess doesn't widen these to `number | undefined`.
  const ch = (i: 0 | 1 | 2) => Math.round(s[i] + (a[i] - s[i]) * t);
  return `#${[ch(0), ch(1), ch(2)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
};

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb | null {
  if (typeof hex !== 'string') return null;
  const digits = hex.replace('#', '').match(/^([\da-f]{3}|[\da-f]{6})$/i)?.[1];
  if (!digits) return null;
  const h =
    digits.length === 3
      ? digits
          .split('')
          .map((c) => c + c)
          .join('')
      : digits;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
